
// CustomersView.tsx
import React, { useMemo } from 'react';
import { useCustomersData } from './hooks/useCustomersData';
import CustomersHeader from './components/CustomersHeader';
import CustomersContent from './components/CustomersContent';
import CustomerEditForm from './CustomerEditForm';
import { Customer } from '@/lib/supabase/customers/types/CustomerTypes';
import { useCustomerFilter } from './hooks/useCustomerFilter';

interface CustomersViewProps {
  handlePageChange?: (pageId: string) => void;
  shopId?: string;
}

const CustomersView = ({ handlePageChange, shopId }: CustomersViewProps) => {
  const { status, setStatus } = useCustomerFilter();
  
  const {
    customers,
    loading,
    selectedCustomers,
    editingCustomerId,
    deleting,
    handleAddCustomer,
    handleEditCustomer,
    handleDeleteCustomer,
    handleBulkDeleteCustomers,
    fetchCustomers,
    toggleCustomerSelection,
    toggleSelectAll,
    areAllSelected,
    setEditingCustomerId,
    formatDate,
    formatPhone,
    shopId: selectedShopId
  } = useCustomersData({ shopId, status });

  // Calcul des compteurs par statut pour l'affichage
  const statusCounts = useMemo(() => {
    // Valeurs par défaut
    const counts = {
      all: customers.length,
      pending: 0,
      paid: 0,
      unpaid: 0
    };
    
    // Au chargement initial, on utilise les compteurs calculés
    if (!loading && customers.length > 0) {
      // Pour les clients "en attente", on vérifie la date de création (7 derniers jours)
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      
      counts.pending = customers.filter(customer => {
        const createdDate = new Date(customer.created_at);
        return createdDate > lastWeek;
      }).length;
      
      // Pour les clients avec commandes payées
      counts.paid = customers.filter(customer => 
        customer.has_paid_orders
      ).length;
      
      // Pour les clients avec commandes non payées
      counts.unpaid = customers.filter(customer => 
        customer.has_unpaid_orders
      ).length;
    }
    
    return counts;
  }, [customers, loading]);

  // Rafraîchir les données après mise à jour d'un client
  const handleCustomerUpdated = () => {
    console.log('[CustomersView] Customer updated, refreshing data');
    fetchCustomers();
    setEditingCustomerId(null);
  };

  // Affichage du formulaire d'édition si un client est en cours d'édition
  if (editingCustomerId) {
    return (
      <CustomerEditForm
        customerId={editingCustomerId}
        onBack={() => setEditingCustomerId(null)}
        onSuccess={handleCustomerUpdated}
        shopId={shopId}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 bg-pastel-blue-light dark:bg-[#121624]">
      <CustomersContent 
        customers={customers as Customer[]}
        loading={loading}
        selectedCustomers={selectedCustomers}
        deleting={deleting}
        onAddCustomer={handleAddCustomer}
        onEditCustomer={handleEditCustomer}
        onDeleteCustomer={handleDeleteCustomer}
        onBulkDelete={handleBulkDeleteCustomers}
        toggleSelection={toggleCustomerSelection}
        toggleSelectAll={toggleSelectAll}
        allSelected={areAllSelected}
        shopId={shopId}
      />
    </div>
  );
};

export default CustomersView;
